import type {
  AppConfig,
  AppProfile,
} from '@stripe-internal/conversations-assistant';
import type {Config} from '@stripe-internal/conversations-shared-dependencies';

const SUPPORT_WIDGET_VIEW_MODE = 'support-widget' as AppConfig['viewMode'];
const MOBILE_ENTRY_POINTS = new Set<Config['entrypoint']>([
  'mobile_ios',
  'mobile_android',
  'react_native_mobile_ios',
  'react_native_mobile_android',
  'mobile_link_ios',
  'mobile_link_android',
]);

export function getDefaultViewMode({
  entryPoint,
  viewMode,
}: {
  entryPoint: Config['entrypoint'];
  viewMode?: Config['viewMode'];
}): AppConfig['viewMode'] {
  if (viewMode) {
    return viewMode as AppConfig['viewMode'];
  }

  if (
    entryPoint.endsWith('support_site') ||
    entryPoint.startsWith('dot_com') ||
    MOBILE_ENTRY_POINTS.has(entryPoint)
  ) {
    return SUPPORT_WIDGET_VIEW_MODE;
  }

  return undefined;
}

function getDefaultInitialVisibility(
  entryPoint: Config['entrypoint'],
  profile: AppProfile,
): AppConfig['initialVisibility'] {
  if (MOBILE_ENTRY_POINTS.has(entryPoint)) {
    return profile.featureFlags.enableLiveChatSupportInMobile
      ? 'open'
      : 'closed';
  }

  return 'closed';
}

export function buildConfig(
  config: Config,
  locale = 'en-US',
): {config: AppConfig; profile: AppProfile} {
  const {
    entrypoint: entryPoint,
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    supportPlan,
    livemode,
  } = config;

  const profile: AppProfile = {
    visitor,
    user,
    merchant,
    featureFlags,
    experiments,
    supportPlan,
    livemode,
  };

  const appConfig: AppConfig = {
    ...config,
    locale,
    entryPoint,
    initialVisibility: getDefaultInitialVisibility(entryPoint, profile),
    closeEndsBotConversation: true,
    viewMode: getDefaultViewMode({
      entryPoint,
      viewMode: config.viewMode,
    }),
  };

  return {config: appConfig, profile};
}
